import { useState, useEffect, useCallback } from "react";
import { FileUpload } from "../components/FileUpload";
import { TextInput } from "../components/TextInput";
import { VehicleInfoForm } from "../components/VehicleInfoForm";
import { ProcessingStatus } from "../components/ProcessingStatus";
import { ReportViewer } from "../components/ReportViewer";
import { processInsuranceClaim } from "../services/api";
import { useCaseHistory, CaseHistory } from "../contexts/CaseHistoryContext";
import { Car, FileText, AlertCircle, ArrowLeft } from "lucide-react";

interface ProcessingState {
  status: "idle" | "processing" | "completed" | "error";
  progress: number;
  currentStep: string;
  results?: any;
  error?: string;
}

interface DashboardPageProps {
  selectedCase?: CaseHistory | null;
  onCaseComplete?: () => void;
}

export function DashboardPage({
  selectedCase,
  onCaseComplete,
}: DashboardPageProps) {
  const { addCase, updateCase } = useCaseHistory();
  const [currentCaseId, setCurrentCaseId] = useState<string | null>(null);

  const [documents, setDocuments] = useState<File[]>([]);
  const [damageImages, setDamageImages] = useState<File[]>([]);
  const [vehicleInfo, setVehicleInfo] = useState("");
  const [location, setLocation] = useState("");
  const [additionalNotes, setAdditionalNotes] = useState("");
  const [requestTramer, setRequestTramer] = useState(false);
  const [vehiclePlate, setVehiclePlate] = useState("");
  const [processing, setProcessing] = useState<ProcessingState>({
    status: "idle",
    progress: 0,
    currentStep: "",
  });

  // Move useCallback to top level - hooks cannot be called conditionally
  const handleVehicleInfoChange = useCallback(
    (val: string) => setVehicleInfo(val),
    []
  );

  // Load selected case if provided
  useEffect(() => {
    if (selectedCase) {
      setProcessing({
        status: selectedCase.status === "completed" ? "completed" : "idle",
        progress: selectedCase.status === "completed" ? 100 : 0,
        currentStep:
          selectedCase.status === "completed" ? "Viewing saved results" : "",
        results: selectedCase.results,
      });
      setCurrentCaseId(selectedCase.id);

      // Restore form data if available
      if (selectedCase.summary.vehicleInfo) {
        setVehicleInfo(selectedCase.summary.vehicleInfo);
      }
      if (selectedCase.summary.location) {
        setLocation(selectedCase.summary.location);
      }
    }
  }, [selectedCase]);

  const handleProcess = async () => {
    if (
      documents.length === 0 &&
      damageImages.length === 0 &&
      !vehicleInfo.trim()
    ) {
      alert(
        "Please provide at least one document, image, or vehicle information to process."
      );
      return;
    }

    // Create case entry in history
    const caseId = await addCase({
      status: "processing",
      summary: {
        vehicleInfo: vehicleInfo.trim() || undefined,
        location: location.trim() || undefined,
        documentsCount: documents.length,
        imagesCount: damageImages.length,
      },
    });
    setCurrentCaseId(caseId);

    setProcessing({
      status: "processing",
      progress: 0,
      currentStep: "Preparing files...",
    });

    try {
      const formData = new FormData();

      documents.forEach((file) => {
        formData.append("documents", file);
      });

      damageImages.forEach((file) => {
        formData.append("damageImages", file);
      });

      if (vehicleInfo.trim()) {
        try {
          const parsedVehicleInfo = JSON.parse(vehicleInfo);
          formData.append(
            "vehicleInfo",
            JSON.stringify(
              Array.isArray(parsedVehicleInfo)
                ? parsedVehicleInfo
                : [parsedVehicleInfo]
            )
          );
        } catch {
          const simpleInfo = [{ description: vehicleInfo }];
          formData.append("vehicleInfo", JSON.stringify(simpleInfo));
        }
      }

      if (location.trim()) {
        formData.append("location", location);
      }

      if (additionalNotes.trim()) {
        formData.append("additionalNotes", additionalNotes);
      }

      formData.append("requestTramer", requestTramer.toString());
      if (requestTramer && vehiclePlate.trim()) {
        formData.append("vehiclePlate", vehiclePlate);
      }

      setProcessing((prev) => ({
        ...prev,
        progress: 20,
        currentStep: "Processing documents...",
      }));

      const results = await processInsuranceClaim(
        formData,
        (progress: number, step: string) => {
          setProcessing((prev) => ({ ...prev, progress, currentStep: step }));
        }
      );

      // Check if n8n returned an error response
      if (results?.status === "error" || results?.error) {
        if (caseId) {
          await updateCase(caseId, { status: "error" });
        }

        setProcessing({
          status: "error",
          progress: 0,
          currentStep: "Processing failed",
          error:
            results.message ||
            results.error ||
            "Analysis failed - n8n workflow error",
        });
        return;
      }

      // Extract license plate from results
      const extractLicensePlate = (res: any): string | undefined => {
        if (!res) return undefined;
        if (res.licensePlate) return res.licensePlate;
        if (Array.isArray(res) && res[0]?.licensePlate)
          return res[0].licensePlate;
        if (res.vehicles?.[0]?.licensePlate)
          return res.vehicles[0].licensePlate;
        if (res.report?.licensePlate) return res.report.licensePlate;
        if (res.vehicleInfo?.licensePlate) return res.vehicleInfo.licensePlate;
        return undefined;
      };

      const licensePlate = extractLicensePlate(results);

      // Update case with results
      if (caseId) {
        try {
          await updateCase(caseId, {
            status: "completed",
            results,
            summary: {
              vehicleInfo: licensePlate || vehicleInfo.trim() || undefined,
              location: location.trim() || undefined,
              documentsCount: documents.length,
              imagesCount: damageImages.length,
              faultPercentage: results?.faultAnalysis?.faultPercentage,
              estimatedCost: results?.costEstimation?.totalCost,
            },
          });
        } catch {
          // Silent failure - case update is not critical
        }
      }

      setProcessing({
        status: "completed",
        progress: 100,
        currentStep: "Processing completed!",
        results,
      });
    } catch (error) {
      // Update case with error
      if (caseId) {
        await updateCase(caseId, {
          status: "error",
        }).catch(() => {}); // Ignore update errors in catch block
      }

      setProcessing({
        status: "error",
        progress: 0,
        currentStep: "Processing failed",
        error:
          error instanceof Error ? error.message : "Unknown error occurred",
      });
    }
  };

  const handleReset = () => {
    setDocuments([]);
    setDamageImages([]);
    setVehicleInfo("");
    setLocation("");
    setAdditionalNotes("");
    setRequestTramer(false);
    setVehiclePlate("");
    setCurrentCaseId(null);
    setProcessing({
      status: "idle",
      progress: 0,
      currentStep: "",
    });
    onCaseComplete?.();
  };

  const handleBackToForm = () => {
    setProcessing({
      status: "idle",
      progress: 0,
      currentStep: "",
    });
    setCurrentCaseId(null);
    onCaseComplete?.();
  };

  return (
    <div className="max-w-6xl mx-auto">
      {/* Hero Section */}
      <div className="text-center mb-10 animate-fade-in">
        <div className="inline-flex items-center justify-center gap-3 mb-4">
          <div className="w-14 h-14 bg-gradient-to-br from-blue-600 to-indigo-600 rounded-2xl flex items-center justify-center shadow-lg shadow-blue-500/30">
            <Car className="w-7 h-7 text-white" />
          </div>
        </div>
        <h1 className="text-3xl md:text-4xl font-bold text-slate-900 mb-3">
          {selectedCase ? "Case Details" : "New Analysis"}
        </h1>
        <p className="text-lg text-slate-600 dark:text-slate-400 max-w-2xl mx-auto leading-relaxed">
          {selectedCase
            ? "Viewing saved analysis results"
            : "Upload your documents and images to get comprehensive insurance reports with Turkish legal compliance."}
        </p>
      </div>

      {processing.status === "idle" && (
        <div className="grid lg:grid-cols-2 gap-8 mb-8">
          {/* Left Column - File Uploads */}
          <div className="space-y-6">
            <div className="card p-6">
              <div className="flex items-center gap-2 mb-4">
                <FileText className="w-5 h-5 text-primary-600 dark:text-primary-400" />
                <h2 className="text-xl font-semibold dark:text-white">Documents</h2>
                <span className="text-sm text-gray-500 dark:text-slate-400">(Optional)</span>
              </div>
              <p className="text-gray-600 dark:text-slate-400 mb-4">
                Upload accident reports, police reports, or any relevant
                documents
              </p>
              <FileUpload
                files={documents}
                onFilesChange={setDocuments}
                accept=".pdf,.doc,.docx,.txt,.jpg,.jpeg,.png"
                multiple
                label="Drop documents here or click to browse"
              />
            </div>

            <div className="card p-6">
              <div className="flex items-center gap-2 mb-4">
                <Car className="w-5 h-5 text-primary-600 dark:text-primary-400" />
                <h2 className="text-xl font-semibold dark:text-white">Damage Images</h2>
                <span className="text-sm text-gray-500 dark:text-slate-400">(Optional)</span>
              </div>
              <p className="text-gray-600 dark:text-slate-400 mb-4">
                Upload photos of vehicle damage for AI-powered assessment
              </p>
              <FileUpload
                files={damageImages}
                onFilesChange={setDamageImages}
                accept=".jpg,.jpeg,.png,.webp"
                multiple
                label="Drop damage photos here or click to browse"
              />
            </div>
          </div>

          {/* Right Column - Text Inputs */}
          <div className="space-y-6">
            <div className="card p-6">
              <h2 className="text-xl font-semibold mb-4 dark:text-white">
                Vehicle Information
              </h2>
              <p className="text-gray-600 dark:text-slate-400 mb-4">
                Add vehicle details for the accident report
              </p>
              <VehicleInfoForm
                value={vehicleInfo}
                onChange={handleVehicleInfoChange}
              />
            </div>

            <div className="card p-6">
              <h2 className="text-xl font-semibold mb-4 dark:text-white">
                Additional Information
              </h2>

              <div className="space-y-4">
                <TextInput
                  value={location}
                  onChange={setLocation}
                  placeholder="e.g., Istanbul, Turkey"
                  label="Accident Location (Optional)"
                  rows={1}
                />

                <TextInput
                  value={additionalNotes}
                  onChange={setAdditionalNotes}
                  placeholder="Any additional context, special circumstances, or notes about the accident..."
                  label="Additional Notes (Optional)"
                  rows={4}
                />

                {/* Tramer Checkbox */}
                <div className="space-y-3">
                  <label className="flex items-center gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={requestTramer}
                      onChange={(e) => setRequestTramer(e.target.checked)}
                      className="w-4 h-4 text-primary-600 border-gray-300 dark:border-slate-600 rounded focus:ring-primary-500"
                    />
                    <span className="text-sm font-medium text-gray-700 dark:text-slate-300">
                      Request Tramer Vehicle History Report
                    </span>
                  </label>
                  <p className="text-xs text-gray-500 ml-7">
                    Get detailed vehicle damage history from Turkish Traffic
                    Insurance Information Center
                  </p>

                  {requestTramer && (
                    <div className="ml-7">
                      <TextInput
                        value={vehiclePlate}
                        onChange={setVehiclePlate}
                        placeholder="34 ABC 123"
                        label="Vehicle Plate Number"
                        rows={1}
                      />
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <div className="flex items-start gap-2">
                <AlertCircle className="w-5 h-5 text-blue-600 mt-0.5" />
                <div className="text-sm text-blue-800">
                  <p className="font-medium mb-1">All inputs are optional!</p>
                  <p>
                    You can process with any combination of documents, images,
                    or text information. The AI will work with whatever you
                    provide.
                  </p>
                </div>
              </div>
            </div>

            <button
              onClick={handleProcess}
              disabled={
                documents.length === 0 &&
                damageImages.length === 0 &&
                !vehicleInfo.trim()
              }
              className="w-full btn btn-primary h-12 text-lg font-medium disabled:opacity-50 disabled:cursor-not-allowed"
            >
              🚀 Process Insurance Claim
            </button>
          </div>
        </div>
      )}

      {/* Processing Status */}
      {processing.status === "processing" && (
        <ProcessingStatus
          progress={processing.progress}
          currentStep={processing.currentStep}
        />
      )}

      {/* Error State */}
      {processing.status === "error" && (
        <div className="card p-6 mb-8 border-red-200 bg-red-50">
          <div className="flex items-center gap-2 mb-4">
            <AlertCircle className="w-6 h-6 text-red-600" />
            <h2 className="text-xl font-semibold text-red-800">
              Processing Failed
            </h2>
          </div>
          <p className="text-red-700 mb-4">{processing.error}</p>
          <button
            onClick={handleReset}
            className="btn btn-outline border-red-300 text-red-700 hover:bg-red-100"
          >
            Try Again
          </button>
        </div>
      )}

      {/* Results */}
      {processing.status === "completed" && processing.results && (
        <div className="space-y-6">
          {selectedCase && (
            <button
              onClick={handleBackToForm}
              className="flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors"
            >
              <ArrowLeft className="w-5 h-5" />
              Geri
            </button>
          )}
          <ReportViewer data={processing.results} onNewAnalysis={handleReset} />
        </div>
      )}
    </div>
  );
}
